import type { JsonValue } from "../document/types.ts";
import type { FieldParse, FieldUnit, SchemaField } from "./schema.ts";

/**
 * The rules of the field kinds past the JSON primitives (ADR-021's amendment "the other field kinds",
 * P1.6-T36), kept apart from the schema's shape so the form, the store and the tests share one reading: how a
 * number with a unit is shown and read back, how a choice is drawn, how long text is counted, how an area of
 * cells is written, what a new list row starts from, how a row moves, and how one field or one row of a
 * thing's fields is read and replaced. Pure: nothing here keeps state.
 */

/**
 * The most options a choice draws as buttons; past it a choice is a list.
 */
export const CHOICE_BUTTONS = 4;

/**
 * How many tiles past its thing's own box an area of cells may be painted, on every side.
 */
export const CELLS_REACH = 16;

/**
 * The most decimals a shown unit value carries.
 */
const MAX_UNIT_DECIMALS = 6;

/**
 * Where a field is: its property's name and, for one row of a list, the row's index.
 */
export interface FieldAddress {
  readonly field: string;
  readonly index?: number;
}

/**
 * How a choice is drawn: buttons while it offers at most `CHOICE_BUTTONS` options, a list past that.
 *
 * @param field The choice.
 * @returns "buttons" or "list".
 */
export function choiceControl(field: SchemaField): "buttons" | "list" {
  return (field.options?.length ?? 0) <= CHOICE_BUTTONS ? "buttons" : "list";
}

/**
 * The option a choice shows as chosen for one thing's value. A stored value is shown as it is. With no value, a
 * required choice shows the default its schema declares when it offers that default, so a thing saved before the
 * field existed reads as the game reads it; a choice that may be empty shows None (""). Display only: the shown
 * default is never written into the document until the author picks an option.
 *
 * @param field The choice.
 * @param value The thing's value, if any.
 * @returns The chosen option, "" for None, or null when none is chosen.
 * @example
 * chosenOption(layoutField, undefined); // "scroll" when the field is required and declares default "scroll"
 */
export function chosenOption(field: SchemaField, value: JsonValue | undefined): string | null {
  if (value !== undefined && value !== null) {
    return String(value);
  }
  if (!field.required) {
    return "";
  }
  const declared = field.defaultValue;
  return typeof declared === "string" && (field.options ?? []).includes(declared) ? declared : null;
}

/**
 * The decimals one stored unit needs to show exactly in the shown unit, at most `MAX_UNIT_DECIMALS`.
 *
 * @param per Stored units in one shown.
 * @returns The decimals.
 */
function unitDecimals(per: number): number {
  for (let decimals = 0; decimals < MAX_UNIT_DECIMALS; decimals += 1) {
    const scaled = 10 ** decimals / per;
    if (Math.abs(scaled - Math.round(scaled)) < 1e-9) {
      return decimals;
    }
  }
  return MAX_UNIT_DECIMALS;
}

/**
 * A stored value in the shown unit, as the field shows it: to the decimals one stored unit needs, trailing
 * zeros dropped.
 *
 * @param value The stored value.
 * @param unit The unit.
 * @returns The text.
 * @example formatUnitValue(30, { symbol: "s", per: 20 }) returns "1.5".
 */
export function formatUnitValue(value: number, unit: FieldUnit): string {
  const text = (value / unit.per).toFixed(unitDecimals(unit.per));
  const trimmed = text.includes(".") ? text.replace(/0+$/, "").replace(/\.$/, "") : text;
  return trimmed === "-0" ? "0" : trimmed;
}

/**
 * Reads text typed in the shown unit as the stored value: an integer is the nearest whole stored unit when the
 * product lies within float noise (or what six places lose) of it, refused otherwise; a number is the product
 * to twelve significant digits. The minimum is checked in stored units and named in shown ones.
 *
 * @param field The field.
 * @param unit Its unit.
 * @param trimmed The typed text, trimmed and not empty.
 * @returns The stored value, or why the text is refused.
 */
export function parseUnitInput(field: SchemaField, unit: FieldUnit, trimmed: string): FieldParse {
  const shown = Number(trimmed);
  if (!Number.isFinite(shown)) {
    return { ok: false, error: `${field.title} must be a number` };
  }
  const product = shown * unit.per;
  if (field.kind !== "integer") {
    return { ok: true, value: Number(product.toPrecision(12)) };
  }
  const whole = Math.round(product);
  if (Math.abs(product - whole) > 1e-6 * Math.max(1, unit.per)) {
    return { ok: false, error: `${field.title} must be a multiple of ${formatUnitValue(1, unit)} ${unit.symbol}` };
  }
  if (field.minimum !== null && whole < field.minimum) {
    return { ok: false, error: `${field.title} must be at least ${formatUnitValue(field.minimum, unit)} ${unit.symbol}` };
  }
  return { ok: true, value: whole === 0 ? 0 : whole };
}

/**
 * Long text's count, measured as the value will be (line breaks as one character each).
 *
 * @param text The text.
 * @param maxLength The field's limit, or null.
 * @returns "42 / 200", or "42 characters" without a limit.
 */
export function textCount(text: string, maxLength: number | null): string {
  const length = text.replace(/\r\n?/g, "\n").length;
  if (maxLength !== null) {
    return `${length} / ${maxLength}`;
  }
  return length === 1 ? "1 character" : `${length} characters`;
}

/**
 * An area as a cells field saves it: unique `[column, rowsUp]` pairs sorted by row, then column.
 *
 * @param cells The cells as `"column,rowsUp"` keys.
 * @returns The pairs, or null for no cell, which is no value.
 */
export function cellsValue(cells: ReadonlySet<string>): [number, number][] | null {
  const pairs = [...cells].map((key) => key.split(",").map(Number) as [number, number]).sort((a, b) => a[1] - b[1] || a[0] - b[0]);
  return pairs.length === 0 ? null : pairs;
}

/**
 * What a new list row starts from: the items' default, else its kind's blank. A reference has none, since a
 * reference row is added by a pick, and nor has a lookup, which no list's rows may be.
 *
 * @param items The field each row is.
 * @returns The value, or null for a reference or a lookup.
 */
export function blankItem(items: SchemaField): JsonValue | null {
  if (items.defaultValue !== null) {
    return items.defaultValue;
  }
  switch (items.kind) {
    case "string":
    case "text":
      return "";
    case "number":
      return 0;
    case "integer":
      return items.minimum ?? 0;
    case "boolean":
      return false;
    case "enum":
      return items.options?.[0] ?? "";
    case "cells":
      return [];
    case "reference":
    case "path":
    case "list":
    case "lookup":
      return null;
  }
}

/**
 * A list with one row moved one place up or down.
 *
 * @param rows The rows.
 * @param index The row's index.
 * @param by -1 to move it up, 1 down.
 * @returns The rows, or null when the row would leave the list.
 */
export function moveRow(rows: readonly JsonValue[], index: number, by: -1 | 1): JsonValue[] | null {
  const to = index + by;
  if (index < 0 || index >= rows.length || to < 0 || to >= rows.length) {
    return null;
  }
  const moved = [...rows];
  [moved[index], moved[to]] = [moved[to] as JsonValue, moved[index] as JsonValue];
  return moved;
}

/**
 * The value at a field's address.
 *
 * @param fields A thing's fields.
 * @param address The field, or one row of it.
 * @returns The value, or null when there is none.
 */
export function fieldValueAt(fields: { readonly [key: string]: JsonValue }, address: FieldAddress): JsonValue | null {
  const value = fields[address.field] ?? null;
  if (address.index === undefined) {
    return value;
  }
  return Array.isArray(value) ? ((value[address.index] as JsonValue | undefined) ?? null) : null;
}

/**
 * A thing's fields with the value at an address replaced: a field set or cleared (null, or an empty list, is
 * no key), a row replaced, appended at the list's end or removed (null). An emptied list is no key.
 *
 * @param fields The fields.
 * @param address The field, or one row of it.
 * @param value The new value, or null to clear the field or remove the row.
 * @returns The new fields, or null when nothing changes.
 * @throws Error when the row lies past the list's end.
 */
export function withFieldValue(fields: { readonly [key: string]: JsonValue }, address: FieldAddress, value: JsonValue | null): { readonly [key: string]: JsonValue } | null {
  const { field, index } = address;
  let next: JsonValue | null = value;
  if (index !== undefined) {
    const current = fields[field];
    const rows: JsonValue[] = Array.isArray(current) ? [...(current as JsonValue[])] : [];
    if (index > rows.length || (index === rows.length && value === null)) {
      throw new Error(`${field} has no row ${index + 1}`);
    }
    if (value === null) {
      rows.splice(index, 1);
    } else {
      rows[index] = value;
    }
    next = rows;
  }
  if (Array.isArray(next) && next.length === 0) {
    next = null;
  }
  if (JSON.stringify(fields[field] ?? null) === JSON.stringify(next)) {
    return null;
  }
  const { [field]: _dropped, ...rest } = fields;
  return next === null ? rest : { ...fields, [field]: next };
}
